import './NewAppointment.scss';

import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import dayjs from 'dayjs';

import { createAppointment } from 'actions/appointments';
import Button from 'atoms/button';
import toast from 'atoms/toast';
import useQuery from 'hooks/useQuery';
import { refreshAppointments } from 'organisms/Appointments/AppointmentActions/util';
import { closeDrawer } from 'store/reducers/ui';
import { capitalizeFirstLetter, isEmpty } from 'util';

import ClientSelect from './ClientSelect';
import DateSelect from './DateSelect';
import GroomerSelect from './GroomerSelect';
import NewAppointmentSuccessModal from './NewAppointmentSuccessModal';
import Notes from './Notes';
import PaymentSelect from './PaymentSelect';
import ServiceSelect from './ServiceSelect';
import ServiceSummary from './ServiceSummary';

export const groomerToOption = (groomer) => {
  if (!groomer) return {};
  return {
    name: capitalizeFirstLetter(groomer.firstName),
    links: groomer.links,
    uuid: groomer.uuid,
  };
};

export default function NewAppointmentDrawer() {
  const dispatch = useDispatch();
  const location = useLocation();
  const query = useQuery();
  const { client, date } = useSelector((state) => state.ui.drawer.data) || {};
  const [selectedClient, setSelectedClient] = useState(
    !!client && !isEmpty(client) ? client : {}
  );
  const [selectedService, setSelectedService] = useState({});
  const { selectedGroomer: currentGroomer } = useSelector((state) => state.ui);
  const { groomers } = useSelector((state) => state.groomers);
  const defaultGroomer =
    currentGroomer?.uuid === 'all'
      ? groomerToOption(groomers?.[0]?.user)
      : currentGroomer;
  const [selectedGroomer, setSelectedGroomer] = useState(defaultGroomer);
  const [selectedDate, setSelectedDate] = useState({
    date:
      !!date && !isEmpty(date) && !!date.date
        ? date.date
        : dayjs().format('YYYY-MM-DD'),
    timeStart:
      !!date && !isEmpty(date) && !!date.timeStart ? date.timeStart : undefined,
  });
  const [note, setNote] = useState('');
  const { locations } = useSelector((state) => state.provider);
  const luid = locations?.[0]?.uuid;
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);

  const canSubmit =
    !isEmpty(selectedClient) &&
    !isEmpty(selectedService) &&
    !isEmpty(selectedGroomer) &&
    !!selectedDate.date &&
    !!selectedDate.timeStart &&
    !!luid;

  const onCreateAppointment = () => {
    dispatch(
      createAppointment({
        animalUuid: selectedClient?.uuid,
        locationUuid: luid,
        locationUserUuid: selectedGroomer?.uuid,
        serviceUuid: selectedService?.uuid,
        userUuid: selectedClient?.owner?.uuid,
        note,
        dateStart: `${selectedDate?.date}T${selectedDate?.timeStart}`,
      })
    ).then(({ success }) => {
      if (success) {
        refreshAppointments({
          location,
          query,
          dispatch,
          auid: selectedClient?.uuid,
        });
        setIsSuccessModalOpen(true);
      } else
        toast({
          text: 'Appointment could not be created, please try again later',
        });
    });
  };

  return (
    <>
      <div className="new-appointment-drawer flex-column">
        <div className="w-100 flex-column">
          <h2 className="f26 mb-24">New appointment</h2>
          <ClientSelect
            selectedClient={selectedClient}
            setSelectedClient={setSelectedClient}
          />
          {!isEmpty(selectedClient) && (
            <>
              <ServiceSelect
                animalTypeId={selectedClient?.type?.id}
                animalBreedId={selectedClient?.breed?.id}
                animalSizeId={selectedClient?.size?.id}
                selectedService={selectedService}
                setSelectedService={setSelectedService}
              />
              {!isEmpty(selectedService) && (
                <>
                  <GroomerSelect
                    animalType={selectedClient?.type?.id}
                    selectedGroomer={selectedGroomer}
                    setSelectedGroomer={setSelectedGroomer}
                  />
                  <DateSelect
                    selectedDate={selectedDate}
                    setSelectedDate={setSelectedDate}
                  />
                  <ServiceSummary selectedServiceUuid={selectedService?.uuid} />
                  <Notes note={note} setNote={setNote} />
                  <PaymentSelect uuid={selectedClient?.owner?.uuid} />
                  <Button
                    text="Create appointment"
                    size="medium"
                    className="align-self-end"
                    onClick={onCreateAppointment}
                    disabled={!canSubmit}
                  />
                </>
              )}
            </>
          )}
        </div>
      </div>
      {isSuccessModalOpen && (
        <NewAppointmentSuccessModal
          isOpen={isSuccessModalOpen}
          onClose={() => {
            setIsSuccessModalOpen(false);
            dispatch(closeDrawer());
          }}
          service={selectedService}
          animal={selectedClient}
          date={selectedDate}
        />
      )}
    </>
  );
}
